import { decimal, label, projectDate } from '@entities/ksp/format';
import type { KspAction, KspWorkspace } from '@entities/ksp/types';
import type { FormField } from '@features/ksp-actions/ActionForm';
import { Button } from '@shared/ui/Button';
import { Card, Muted, Row, Small } from '../styles';
import { choice, dateField, sourceText, textField } from '../types';
import { PagedTable } from '../components/CollectionTable';

export interface ActualTabProps {
  workspace: KspWorkspace;
  projectId: string;
  versionId?: string | null;
  timezone: string;
  permission: (action?: KspAction) => boolean;
  open: (
    title: string,
    fields: FormField[],
    action: (v: Record<string, string>) => Promise<unknown>,
    submit?: string,
  ) => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
  workOptions: { value: string; label: string }[];
  fileOptions: { value: string; label: string }[];
}

export function ActualTab({
  workspace: d,
  projectId,
  versionId,
  timezone,
  permission,
  open,
  execute,
  workOptions,
  fileOptions,
}: ActualTabProps) {
  return (
    <>
      <Card>
        <Row>
          <h3>Приёмка выполнения ПТО</h3>
          <Button
            disabled={!permission()}
            onClick={() =>
              open(
                'Ручной факт ПТО',
                [
                  choice('activity_id', 'Работа', workOptions),
                  dateField('performed_date', 'Дата выполнения (смена)'),
                  textField('quantity', 'Выполненное количество'),
                  textField('unit', 'Единица'),
                  textField('provenance', 'Первичный источник и основание'),
                  choice('evidence_file_id', 'Подтверждающий документ', fileOptions),
                ],
                (values) =>
                  execute(`projects/${projectId}/actual-submissions`, {
                    ...values,
                    version_id: versionId,
                    source_kind: 'manual',
                    provenance: { description: values.provenance },
                  }),
              )
            }
          >
            Добавить факт
          </Button>
        </Row>
        <Muted>
          В показатели входит только принятый факт. Отсутствие рапорта сохраняет неизвестное
          выполнение; корректировка принятого объёма — отдельная компенсирующая запись.
        </Muted>
        <PagedTable
          rows={d.actual_submissions}
          columns={[
            {
              name: 'Работа и дата',
              value: (fact) => (
                <>
                  {d.activities.find((a) => a.id === fact.activity_id)?.code} ·{' '}
                  {fact.performed_date}
                  <Small>Приёмка {projectDate(fact.accepted_at, timezone)}</Small>
                </>
              ),
            },
            { name: 'Количество', value: (fact) => `${decimal(fact.quantity)} ${fact.unit}` },
            {
              name: 'Источник',
              value: (fact) => (
                <>
                  {label(fact.source_kind)}
                  <Small>
                    {sourceText(fact.source_ref)} · {fact.evidence}
                  </Small>
                  {fact.status === 'duplicate_review' && (
                    <Small>
                      Изменение объёма: {decimal(fact.provenance?.proposed_delta)} {fact.unit}
                      .
                      {fact.provenance?.date_changed &&
                        ` Новая дата источника: ${fact.performed_date}; подтвердите её явно.`}
                      {fact.provenance?.source_snapshot_id &&
                        ` Снимок источника: ${fact.provenance.source_snapshot_id}.`}
                    </Small>
                  )}
                </>
              ),
            },
            { name: 'Статус', value: (fact) => label(fact.status) },
            {
              name: 'Решение',
              value: (fact) =>
                fact.status === 'duplicate_review' ? (
                  <Row>
                    <Button
                      $size="sm"
                      disabled={
                        !permission('actual.correct') || !fact.provenance?.initial_ledger_id
                      }
                      onClick={() =>
                        open(
                          'Принять изменение источника',
                          [
                            textField(
                              'delta',
                              'Изменение объёма (+ / −)',
                              true,
                              fact.provenance?.proposed_delta ?? '',
                            ),
                            ...(fact.provenance?.date_changed
                              ? [
                                  {
                                    ...dateField(
                                      'performed_date',
                                      'Подтверждённая дата выполнения',
                                    ),
                                    help: `Дата в новой ревизии источника: ${fact.performed_date}. Укажите её после проверки.`,
                                  },
                                ]
                              : []),
                            textField('reason', 'Причина и основание приёмки изменения'),
                            choice('evidence_file_id', 'Доказательство', fileOptions),
                          ],
                          (values) =>
                            execute(
                              `actual-ledger/${fact.provenance?.initial_ledger_id}/corrections`,
                              { ...values, source_submission_id: fact.id },
                            ),
                        )
                      }
                    >
                      Принять изменение источника
                    </Button>
                    <Button
                      $size="sm"
                      disabled={!permission('actual.accept')}
                      onClick={() =>
                        open(
                          'Вернуть изменение источника',
                          [textField('comment', 'Основание возврата')],
                          (values) =>
                            execute(`actual-submissions/${fact.id}/review`, {
                              ...values,
                              status: 'returned',
                            }),
                        )
                      }
                    >
                      Вернуть
                    </Button>
                  </Row>
                ) : fact.status === 'accepted' ? (
                  <Button
                    $size="sm"
                    disabled={
                      !permission('actual.correct') ||
                      !(
                        fact.ledger_id ??
                        d.ledger?.find((entry) => entry.submission_id === fact.id)?.id
                      )
                    }
                    onClick={() =>
                      open(
                        'Компенсирующая корректировка',
                        [
                          textField('delta', 'Изменение объёма (+ / −)'),
                          textField('reason', 'Причина'),
                          choice('evidence_file_id', 'Доказательство', fileOptions),
                        ],
                        (values) =>
                          execute(
                            `actual-ledger/${fact.ledger_id ?? d.ledger?.find((entry) => entry.submission_id === fact.id)?.id}/corrections`,
                            values,
                          ),
                      )
                    }
                  >
                    Корректировка
                  </Button>
                ) : (
                  <Button
                    $size="sm"
                    disabled={!permission('actual.accept')}
                    onClick={() =>
                      open(
                        'Решение по факту',
                        [
                          choice('status', 'Результат', [
                            { value: 'accepted', label: 'Принять' },
                            { value: 'returned', label: 'Вернуть' },
                          ]),
                          textField('comment', 'Основание решения'),
                        ],
                        (values) => execute(`actual-submissions/${fact.id}/review`, values),
                      )
                    }
                  >
                    Принять / вернуть
                  </Button>
                ),
            },
          ]}
        />
      </Card>
      <Card>
        <h3>Принятый факт — неизменяемый реестр</h3>
        <PagedTable
          rows={d.ledger ?? []}
          columns={[
            {
              name: 'Работа / выполнение',
              value: (entry) => (
                <>
                  {d.activities.find((activity) => activity.id === entry.activity_id)?.code} ·{' '}
                  {entry.performed_date}
                  <Small>Приёмка {projectDate(entry.accepted_at, timezone)}</Small>
                </>
              ),
            },
            {
              name: 'Количество',
              value: (entry) => `${decimal(entry.quantity)} ${entry.unit}`,
            },
            {
              name: 'Источник',
              value: (entry) => (
                <>
                  {label(entry.source_kind)}
                  <Small>
                    {entry.canonical_source_id} · {sourceText(entry.source_ref)}
                  </Small>
                </>
              ),
            },
            {
              name: 'Корректировка',
              value: (entry) =>
                entry.correction_of ? (
                  <Small>Компенсация записи {entry.correction_of}</Small>
                ) : (
                  <Button
                    $size="sm"
                    disabled={!permission('actual.correct')}
                    onClick={() =>
                      open(
                        'Компенсирующая корректировка принятого факта',
                        [
                          textField('delta', 'Изменение объёма (+ / −)'),
                          textField('reason', 'Причина'),
                          choice('evidence_file_id', 'Доказательство', fileOptions),
                        ],
                        (values) => execute(`actual-ledger/${entry.id}/corrections`, values),
                      )
                    }
                  >
                    Корректировка
                  </Button>
                ),
            },
          ]}
        />
      </Card>
    </>
  );
}
